import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaShieldAlt,
  FaUserCheck,
  FaBell,
  FaLock,
  FaCreditCard,
  FaCheckDouble,
  FaUsers,
  FaHandHoldingHeart,
  FaEnvelope,
  FaRupeeSign,
  FaArrowRight,
  FaRedo,
  FaTimesCircle,
  FaMinusCircle,
  FaClock,
} from 'react-icons/fa';
import axios from 'axios';
import Modal from 'react-modal';
import { loadRazorpayScript } from '../utils/loadRazorpay';
import { submitForManualReview } from '../services/verificationReview';
import './NomineeCheckPage.css';
import Seo from '../components/Seo';

Modal.setAppElement('#root');

function parseAmountPaise(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function formatRupeeLabel(amountInPaise) {
  const rupees = amountInPaise / 100;
  return rupees.toLocaleString('en-IN', {
    maximumFractionDigits: rupees % 1 === 0 ? 0 : 2,
  });
}

function getChannelStepMeta(status, channel) {
  const isEmail = channel === 'email';
  if (status === 'SENT') {
    return {
      title: isEmail ? 'Emails Delivered' : 'SMS Sent',
      description: isEmail
        ? 'Detailed claim information sent to all nominee email addresses'
        : 'Instant notifications delivered to all nominee phone numbers',
      icon: isEmail ? FaEnvelope : FaBell,
      color: isEmail ? '#3b82f6' : '#10b981',
      cardClass: 'step-card-success',
    };
  }
  if (status === 'NOT_APPLICABLE') {
    return {
      title: isEmail ? 'Email Not Applicable' : 'SMS Not Available',
      description: isEmail
        ? 'No nominee email addresses were available to notify'
        : 'SMS is not enabled yet — nominees were notified by email only',
      icon: FaMinusCircle,
      color: '#94a3b8',
      cardClass: 'step-card-neutral',
    };
  }
  return {
    title: isEmail ? 'Email Failed' : 'SMS Failed',
    description: isEmail
      ? 'Could not deliver emails to nominee email addresses'
      : 'Could not deliver SMS to nominee phone numbers',
    icon: FaTimesCircle,
    color: '#ef4444',
    cardClass: 'step-card-failed',
  };
}

function NomineeCheckPage({ user }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [toast, setToast] = useState({ type: "", message: "" });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    phone: '',
  });
  const toastRef = useRef();
  const [isSecureMode, setIsSecureMode] = useState(false);
  const [, setSecureFetchLoading] = useState(false);
  const [showSecureConfirm, setShowSecureConfirm] = useState(false);
  const [verificationCheckId, setVerificationCheckId] = useState(null);
  const [notificationResult, setNotificationResult] = useState(null);
  const [notificationSending, setNotificationSending] = useState(false);

  const baseAmountInPaise = parseAmountPaise(process.env.REACT_APP_NOMINEE_CHECK_BASE_AMOUNT_PAISE, 200000);
  const secureAmountInPaise = parseAmountPaise(process.env.REACT_APP_NOMINEE_CHECK_SECURE_AMOUNT_PAISE, 2000000);
  const skipNomineePayment = process.env.REACT_APP_SKIP_NOMINEE_PAYMENT === 'true';
  const effectiveAmountInPaise = isSecureMode ? secureAmountInPaise : baseAmountInPaise;
  const paymentAmountLabel = formatRupeeLabel(effectiveAmountInPaise);

  // Focus toast on message change for accessibility
  useEffect(() => {
    if (toast.message && toastRef.current) {
      toastRef.current.focus();
    }
  }, [toast]);

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return '';
    if (email.length > 50)
      return "Email can't exceed more than 50 characters";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address.';
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone) return '';
    if (!/^[0-9]{10}$/.test(phone)) return 'Phone number must be exactly 10 digits.';
    if (phone.startsWith('0') || phone.startsWith('1')) return 'Phone number should not start with 0 or 1.';
    return '';
  };

  // Input handlers with validation
  const handleEmailChange = (value) => {
    setEmail(value);
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: validateEmail(value) }));
    }
  };

  // Lookup secure mode for the policyholder being validated using email/phone
  const checkSecureModeForIdentifier = async (checkId = verificationCheckId) => {
    const token = localStorage.getItem('token');
    try {
      setSecureFetchLoading(true);
      const params = new URLSearchParams();
      if (email) params.append('email', email);
      if (phone) params.append('phoneNumber', phone);
      const url = `${process.env.REACT_APP_HOST_SERVER}/api/user/secure-mode/lookup${params.toString() ? `?${params.toString()}` : ''}`;
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (!resp.ok) throw new Error('Lookup failed');
      const json = await resp.json();
      const raw =
        (json && (json.isSecure ?? json.secure)) ??
        (json?.data && (json.data.isSecure ?? json.data.secure));
      const value = raw === true || raw === false ? raw : String(raw).toLowerCase() === 'true';
      setIsSecureMode(Boolean(value));
      if (Boolean(value)) setShowSecureConfirm(true);

      if (checkId) {
        await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/nominee-verifications/${checkId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            secureMode: Boolean(value),
            amountExpectedPaise: Boolean(value) ? secureAmountInPaise : baseAmountInPaise,
          }),
        });
      }
    } catch (e) {
      console.error('Secure-mode lookup error:', e);
    } finally {
      setSecureFetchLoading(false);
    }
  };

  // Form submit handler for validation API
  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast({ type: "", message: "" });

    const emailError = email ? validateEmail(email) : '';
    const phoneError = phone ? validatePhone(phone) : '';

    if (!email.trim() && !phone.trim()) {
      setToast({ type: "error", message: "Please enter either Email or Phone Number." });
      return;
    }

    setErrors({
      email: emailError,
      phone: phoneError,
    });

    if (emailError || phoneError) {
      setToast({ type: "error", message: "Please fix the errors in the form before submitting." });
      return;
    }

    setLoading(true);

    const formData = new FormData();
    if (email) formData.append("email", email);
    if (phone) formData.append("phoneNumber", phone);
    const token = localStorage.getItem('token');

    try {
      const response = await axios.post(`${process.env.REACT_APP_HOST_SERVER}/api/policies/policyholders/validate`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = response.data;

      if (data.successful === true && data.message === "User/Nominee Found") {
        const nextCheckId = data.data?.verificationCheckId ?? null;
        setVerificationCheckId(nextCheckId);
        setToast({ type: "success", message: "User/Nominee Found. Proceeding..." });
        setStep(2);
        checkSecureModeForIdentifier(nextCheckId);
      } else {
        if (data.data?.verificationCheckId) {
          setVerificationCheckId(data.data.verificationCheckId);
        }
        setToast({ type: "error", message: data.message || "Validation failed. Please check your details." });
      }
    } catch (error) {
      setToast({ type: "error", message: "Error calling validation API. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Razorpay payment handler
  const sendNomineeNotification = async () => {
    const token = localStorage.getItem('token');
    const emailRequestBody = {
      emailId: email || null,
      phoneNumber: phone || null,
      verificationCheckId: verificationCheckId || null,
    };

    setNotificationSending(true);
    try {
      const sendResponse = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(emailRequestBody),
      });

      const json = await sendResponse.json().catch(() => ({}));
      const result = json.data && typeof json.data === 'object' ? json.data : null;

      if (!result?.emailStatus) {
        const notification = {
          emailStatus: 'FAILED',
          smsStatus: 'NOT_APPLICABLE',
          emailMessage: json.message || 'Email API did not return delivery status. Restart the backend.',
          smsMessage: 'SMS notifications are not enabled yet.',
          overallSuccess: false,
        };
        setNotificationResult(notification);
        setToast({
          type: 'error',
          message: notification.emailMessage,
        });
        return notification;
      }

      const notification = {
        emailStatus: result.emailStatus,
        smsStatus: result.smsStatus || 'NOT_APPLICABLE',
        emailMessage: result.emailMessage || '',
        smsMessage: result.smsMessage || 'SMS notifications are not enabled yet.',
        overallSuccess: Boolean(result.overallSuccess),
      };
      setNotificationResult(notification);

      if (!notification.overallSuccess) {
        setToast({
          type: 'error',
          message: json.message || 'Payment verified but notification delivery failed.',
        });
      }

      return notification;
    } catch (err) {
      const failed = {
        emailStatus: 'FAILED',
        smsStatus: 'NOT_APPLICABLE',
        emailMessage: '',
        smsMessage: '',
        overallSuccess: false,
      };
      setNotificationResult(failed);
      setToast({
        type: 'error',
        message: 'Payment verified but error occurred sending notification.',
      });
      return failed;
    } finally {
      setNotificationSending(false);
    }
  };

  const completePaymentAndNotify = async (successMessage) => {
    setToast({ type: 'success', message: successMessage });
    let notification;
    if (isSecureMode) {
      setNotificationSending(true);
      try {
        notification = await submitForManualReview(verificationCheckId, localStorage.getItem('token'));
      } catch (err) {
        notification = {
          reviewPending: false,
          overallSuccess: false,
          emailStatus: 'FAILED',
          smsStatus: 'NOT_APPLICABLE',
          emailMessage: err.message || 'Payment received but manual review submission failed.',
          smsMessage: 'SMS notifications are not enabled yet.',
        };
        setToast({ type: 'error', message: notification.emailMessage });
      } finally {
        setNotificationSending(false);
      }
    } else {
      notification = await sendNomineeNotification();
    }
    setNotificationResult(notification);
    setStep(3);
    return notification;
  };

  const handlePayment = async (amountPaise) => {
    const token = localStorage.getItem('token');
    setPaymentLoading(true);
    setToast({ type: '', message: '' });

    if (skipNomineePayment) {
      try {
        const createOrderResponse = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/orders/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: user.id,
            serviceType: 'nominee_check',
            pickupDate: new Date().toISOString(),
            amount: amountPaise,
            verificationCheckId,
          }),
        });

        if (!createOrderResponse.ok) {
          const errorText = await createOrderResponse.text();
          setToast({ type: 'error', message: 'Order creation failed: ' + errorText });
          setPaymentLoading(false);
          return;
        }

        const responseData = await createOrderResponse.json();
        if (responseData?.data?.skipPayment) {
          await completePaymentAndNotify('Payment skipped (local dev mode).');
        } else {
          setToast({
            type: 'info',
            message: 'Set nominee.check.skip-payment=true on the backend to skip Razorpay locally.',
          });
        }
      } catch (error) {
        setToast({ type: 'error', message: 'Payment failed: ' + error.message });
      } finally {
        setPaymentLoading(false);
      }
      return;
    }

    const res = await loadRazorpayScript();
    if (!res) {
      setToast({ type: 'error', message: 'Failed to load Razorpay SDK. Please check your internet connection.' });
      setPaymentLoading(false);
      return;
    }

    try {
      const createOrderResponse = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/orders/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
         },
        body: JSON.stringify({
          userId: user.id,
          serviceType: 'nominee_check',
          pickupDate: new Date().toISOString(),
          amount: amountPaise,
          verificationCheckId,
        }),
      });

      if (!createOrderResponse.ok) {
        const errorText = await createOrderResponse.text();
        setToast({ type: 'error', message: 'Order creation failed: ' + errorText });
        setPaymentLoading(false);
        return;
      }

      const responseData = await createOrderResponse.json();

      const razorpayOrderId = responseData?.data?.razorpayOrderId;
      const currency = responseData?.data?.currency;
      const amount = responseData?.data?.amount;

      if (responseData?.data?.skipPayment) {
        await completePaymentAndNotify('Payment skipped (local dev mode).');
        setPaymentLoading(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount,
        currency,
        name: 'KeepMyAsset',
        description: 'Nominee Check Payment',
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            const verifyResponse = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/orders/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
               },
              body: JSON.stringify({
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              }),
            });

            if (verifyResponse.ok) {
              await completePaymentAndNotify('Payment successful and verified!');
            } else {
              setToast({ type: 'error', message: 'Payment verification failed.' });
            }
          } catch (err) {
            setToast({ type: 'error', message: 'Error verifying payment.' });
          } finally {
            setPaymentLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            setToast({ type: 'info', message: 'Payment popup closed.' });
            setPaymentLoading(false);
          },
        },
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.contact,
        },
        theme: {
          color: '#3399cc',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      setToast({ type: 'error', message: 'Payment failed: ' + error.message });
      setPaymentLoading(false);
    }
  };

  const handlePayClick = () => {
    if (isSecureMode) {
      setShowSecureConfirm(true);
      return;
    }
    handlePayment(effectiveAmountInPaise);
  };

  const handleReset = () => {
    setEmail("");
    setPhone("");
    setStep(1);
    setToast({ type: "", message: "" });
    setErrors({ email: '', phone: '' });
    setVerificationCheckId(null);
    setNotificationResult(null);
    setNotificationSending(false);
    setIsSecureMode(false);
    setShowSecureConfirm(false);
  };

  return (
    <>
    {notificationSending && (
      <div className="notification-fullscreen-overlay" role="alert" aria-live="assertive" aria-busy="true">
        <div className="notification-fullscreen-content">
          <FaSpinner className="notification-fullscreen-spinner" aria-hidden="true" />
          <h2>{isSecureMode ? 'Submitting for manual review' : 'Sending notifications'}</h2>
          <p>
            {isSecureMode
              ? 'Please wait while we queue this secure-mode request for admin verification…'
              : 'Please wait while we notify nominees by email and SMS…'}
          </p>
        </div>
      </div>
    )}
    <Seo
      title="Verify & Notify Nominees India"
      description="Locate account holder records and notify registered nominees through KeepMyAsset's secure family verification portal."
      path="/nominee-check"
      keywords="nominee check India, notify nominees, nominee verification, family asset notification, keepmyasset nominee"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'KeepMyAsset Notify All Nominees',
        description:
          'Secure portal for families to locate account holder records and notify all registered nominees in India.',
        provider: {
          '@type': 'Organization',
          name: 'KeepMyAsset',
          url: 'https://www.keepmyasset.com',
        },
        areaServed: {
          '@type': 'Country',
          name: 'India',
        },
      }}
    />
    <div className="nominee-check-page">
      {/* Hero Section */}
      {step === 1 && (
        <section className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">
              <FaHandHoldingHeart style={{ marginRight: '1rem', color: '#fbbf24' }} />
              Notify All Nominees Portal
            </h1>
            <p className="hero-description">
              Enter the account holder&apos;s registered email to find their records
              and send notifications to every nominee they registered on KeepMyAsset.
            </p>

            <div className="hero-features">
              <div className="hero-feature">
                <FaShieldAlt className="hero-feature-icon" />
                <h4>Secure & Private</h4>
                <p>Bank-level encryption protects account holder and nominee information</p>
              </div>
              <div className="hero-feature">
                <FaUserCheck className="hero-feature-icon" />
                <h4>Account Holder Lookup</h4>
                <p>Confirm the deceased account holder&apos;s records before proceeding</p>
              </div>
              <div className="hero-feature">
                <FaBell className="hero-feature-icon" />
                <h4>Notify Every Nominee</h4>
                <p>All registered nominees receive email alerts with claim guidance</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Form Section */}
      <section className="search-results">
        <div className="results-container">
          <div className="results-header">
            <h2>
              <FaUsers style={{ color: '#3b82f6' }} />
              Notify All Nominees
            </h2>
            <div className="step-indicator">
              Step {step} of 3
            </div>
          </div>

          {/* Toast Notification */}
          {toast.message && (
            <div
              className={`toast-notification ${toast.type}`}
              tabIndex={-1}
              ref={toastRef}
              aria-live="assertive"
              role="alert"
            >
              {toast.type === 'error' && <FaExclamationTriangle />}
              {toast.type === 'success' && <FaCheckCircle />}
              {toast.type === 'info' && <FaInfoCircle />}
              {toast.message}
            </div>
          )}

          {/* Step 1: Upload Form */}
          {step === 1 && (
            <div className="upload-form-container">
              <form onSubmit={handleSubmit} className="upload-form">
                <div className="form-section">
                  <h3>
                    <FaUsers style={{ color: '#3b82f6' }} />
                    Account Holder Details
                  </h3>
                  <p style={{ color: '#64748b', marginTop: '-6px', marginBottom: '12px' }}>
                    Enter the account holder&apos;s registered email address to locate their policy records and notify all registered nominees.
                  </p>
                  <p style={{ color: '#64748b', marginBottom: '12px', fontSize: '0.92rem' }}>
                    Need to find records by nominee email or phone instead?{' '}
                    <Link to="/nominee-lookup">Use Nominee Lookup</Link>.
                  </p>

                  <div className="input-row-horizontal">
                    <div className="input-group">
                      <label>
                        <FaEnvelope style={{ color: '#3b82f6' }} />
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => handleEmailChange(e.target.value)}
                        placeholder="e.g. john.doe@example.com"
                        className={errors.email ? 'input-error' : ''}
                        maxLength={50}
                      />
                      {errors.email && <div className="error-message"><FaExclamationTriangle />{errors.email}</div>}
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="submit-btn"
                >
                  {loading ? (
                    <>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <FaCheckDouble />
                      Verify & Proceed
                      <FaArrowRight />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <div className="payment-container">
              <div style={{ marginBottom: '3rem' }}>
                <FaCreditCard style={{
                  fontSize: '5rem',
                  color: '#3b82f6',
                  marginBottom: '1.5rem'
                }} />
                <h3>Secure Payment Required</h3>
                <p>Complete the payment to notify all registered nominees</p>
              </div>

              <div className="payment-amount">
                <FaRupeeSign />
                Payment Amount: ₹{paymentAmountLabel}
                {skipNomineePayment ? ' (dev: payment skipped)' : ''}
              </div>

              <div className="payment-features">
                <div className="payment-feature">
                  <FaBell className="payment-feature-icon" style={{ color: '#10b981' }} />
                  <h4>SMS Notifications</h4>
                  <p>Instant alerts to all nominees</p>
                </div>
                <div className="payment-feature">
                  <FaEnvelope className="payment-feature-icon" style={{ color: '#3b82f6' }} />
                  <h4>Email Notifications</h4>
                  <p>Detailed claim information</p>
                </div>
                <div className="payment-feature">
                  <FaLock className="payment-feature-icon" style={{ color: '#ef4444' }} />
                  <h4>Secure Process</h4>
                  <p>Bank-level encryption</p>
                </div>
              </div>

              <button
                onClick={handlePayClick}
                disabled={paymentLoading}
                className="submit-btn"
                style={{
                  maxWidth: '500px',
                  margin: '0 auto'
                }}
              >
                {paymentLoading ? (
                  <>
                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <FaCreditCard />
                    {skipNomineePayment ? 'Continue (skip payment)' : `Pay ₹${paymentAmountLabel} Securely`}
                    <FaArrowRight />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="success-container">
              <div className="success-message">
                {notificationResult?.reviewPending ? (
                  <FaShieldAlt className="success-icon" style={{ color: '#2563eb' }} />
                ) : notificationResult?.overallSuccess ? (
                  <FaCheckCircle className="success-icon" />
                ) : (
                  <FaExclamationTriangle className="success-icon" style={{ color: '#ef4444' }} />
                )}
                <h3>
                  {notificationResult?.reviewPending
                    ? 'Submitted for Manual Review'
                    : notificationResult?.overallSuccess
                      ? 'Payment Successful!'
                      : 'Payment Complete — Notification Issues'}
                </h3>
                <p>
                  {notificationResult?.reviewPending
                    ? 'Secure Mode is enabled for this account. KeepMyAsset will manually verify this request before sharing any document details with the requester.'
                    : notificationResult?.overallSuccess
                      ? 'Nominee notifications were processed. See the delivery status for each channel below.'
                      : 'Payment was completed, but one or more notification channels failed. Review the status cards below.'}
                </p>
              </div>

              <div className="success-steps">
                {notificationResult?.reviewPending ? (
                  <>
                    <div className="step-card step-card-review">
                      <FaShieldAlt className="step-icon" style={{ color: '#2563eb' }} />
                      <h4>Awaiting admin review</h4>
                      <p>{notificationResult.emailMessage}</p>
                    </div>
                    <div className="step-card step-card-review">
                      <FaClock className="step-icon" style={{ color: '#f59e0b' }} />
                      <h4>Track in Verification History</h4>
                      <p>You can see this request as &quot;Awaiting manual review&quot; in your Verification History until an admin approves or rejects it.</p>
                    </div>
                  </>
                ) : (
                  <>
                {['sms', 'email'].map((channel) => {
                  const status = notificationResult?.[`${channel}Status`] || 'FAILED';
                  const meta = getChannelStepMeta(status, channel);
                  const StepIcon = meta.icon;
                  const detailMessage = notificationResult?.[`${channel}Message`];

                  return (
                    <div key={channel} className={`step-card ${meta.cardClass}`}>
                      <StepIcon className="step-icon" style={{ color: meta.color }} />
                      <h4>{meta.title}</h4>
                      <p>{detailMessage || meta.description}</p>
                    </div>
                  );
                })}
                <div className={`step-card ${notificationResult?.overallSuccess ? 'step-card-success' : 'step-card-failed'}`}>
                  <FaHandHoldingHeart
                    className="step-icon"
                    style={{ color: notificationResult?.overallSuccess ? '#f59e0b' : '#ef4444' }}
                  />
                  <h4>{notificationResult?.overallSuccess ? 'Families Informed' : 'Families Not Fully Informed'}</h4>
                  <p>
                    {notificationResult?.overallSuccess
                      ? 'Beneficiaries can now proceed with their rightful claims'
                      : 'Please retry notification delivery or contact support if the issue persists'}
                  </p>
                </div>
                  </>
                )}
              </div>

              <button
                onClick={handleReset}
                className="submit-btn"
                style={{
                  width: 'auto',
                  display: 'inline-flex',
                  maxWidth: '400px',
                  margin: '0 auto'
                }}
              >
                <FaRedo />
                Process Another Verification
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Secure Mode Confirmation Modal - must be outside step blocks so it's always mounted */}
      <Modal
        isOpen={showSecureConfirm}
        onRequestClose={() => setShowSecureConfirm(false)}
        contentLabel="Secure Mode Confirmation"
        className="modal"
        overlayClassName="modal-overlay"
      >
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <FaLock style={{ color: '#16a34a' }} /> Secure Mode Is Enabled
        </h2>
        <div style={{ color: '#334155', lineHeight: 1.6 }}>
          <p>
            Because Secure Mode is ON for this account, we will perform additional manual verification
            before notifying nominees. This ensures extra protection against misuse and false notifications.
          </p>
          <p style={{ marginTop: '0.5rem' }}>
            Due to the manual review effort, the verification fee is{' '}
            <strong>₹{formatRupeeLabel(secureAmountInPaise)}</strong> instead of ₹{formatRupeeLabel(baseAmountInPaise)}.
          </p>
        </div>
        <div className="modal-buttons" style={{ marginTop: '1rem' }}>
          <button
            type="button"
            className="cancel-btn"
            onClick={() => { setShowSecureConfirm(false); handleReset(); }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="add-btn"
            onClick={() => { setShowSecureConfirm(false); handlePayment(secureAmountInPaise); }}
          >
            Continue & Pay ₹{formatRupeeLabel(secureAmountInPaise)}
          </button>
        </div>
      </Modal>
    </div>
    </>
  );
}

export default NomineeCheckPage;